/*
 * File: index.ts
 * File Created: 2023-12-18 05:36:24
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-12-26 09:43:09
 * Modified By: Val Liu
 * -----
 */

//import * as Rx from 'rxjs/Rx';
import { Vue } from "vue-property-decorator";
import { observeOn, filter, first, map } from "rxjs/operators";
import { BehaviorSubject, Subject, asapScheduler, Observable } from "rxjs";

import { request } from "./../utility/request";
import WSSocket from "isomorphic-ws";
import config from "@/config/default/serverConfig";
const debug = require("@/config/default/debug");

/// bridge Log /////////////////////
import * as Print from "./../../utilities/print";
import { Url } from "@/../core/utilities/url-join";
import { IInputFileUnit } from "@root/components/inputs/input-file/lib/core";
import {
    VueHolder,
    extractErrorCode,
    extractErrorFields,
} from "@root/core/utilities";
import { resolveGlobalErrorMessage } from "@root/core/utilities";
import { cache } from "@root/core/decorators/cache";
let Log = {
    Info: (title: string, message: string) => Print.Message(title, message, "info"),
    Error: (title: string, message: string) => Print.Message(title, message, "error"),
};
////////////////////////////////////

type ParseObject<T> = T;
type IncomingMessage = any;

export async function retry<T>(
    func: (
        resolve: (value?: T | PromiseLike<T>) => void,
        reject: (reason?: any) => void,
    ) => void | Promise<void>,
    times: number = 10,
    hintname: string | null = null,
    rejectFilter: ((e: any) => boolean) | null = null,
): Promise<T> {
    let count: number = 0;
    let err;
    return new Promise<T>(async (resolve, reject) => {
        do {
            try {
                return resolve(
                    await new Promise<T>((resolve, reject) =>
                        func(resolve as any, reject),
                    ),
                );
            } catch (e) {
                err = e;
            }

            /// error log
            let test = Math.log10(count);
            if (test > 0 && Number.isInteger(test))
                Log.Error(
                    "Critical Error",
                    `${
                        hintname ? `Function <${hintname}> ` : ""
                    }Retry ${count} times with error: ${err}`,
                );
            /// reject when there is an error happens
            if (rejectFilter && rejectFilter(err)) break;
        } while (times === 0 || ++count < times);
        return reject(err);
    });
}

export class Socket {
    io: WSSocket;
    private sendCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    static get(ws: WSSocket): any;
    static get(arg1: any, arg2?: any): Promise<Socket | null> {
        return new Promise((resolve) => {
            var ws: any, info: any, cb;
            if (!arg2) {
                if (arg1 instanceof WSSocket) {
                    ws = arg1;
                    info = {};
                    cb = (c: any) => {
                        c(ws);
                    };
                } else {
                    ws = (<any>arg1)._websocket; /// check response
                    if (!ws) {
                        resolve(null);
                        return;
                    }
                    info = ws.info;
                    cb = ws.cb;
                }
            } else {
                info = arg1;
                cb = arg2;
            }
            if (info.vsocket) {
                resolve(info.vsocket);
                return;
            }
            cb((socket: any) => {
                info.vsocket = new Socket(socket);
                resolve(info.vsocket);
            });
        });
    }

    private constructor(socket: WSSocket) {
        this.io = socket;
    }

    sendPromise(data: any): Promise<void> {
        return new Promise((resolve, reject) => {
            this.send(data, (err) => {
                if (!err) return resolve();
                reject(err);
            });
        });
    }

    send(data: any, cb?: (err: Error) => void): void;
    send(
        data: any,
        options: { mask?: boolean; binary?: boolean },
        cb?: (err: Error) => void,
    ): void;
    send(data: any, arg2: any, arg3?: any) {
        typeof data === "object" && (data = JSON.stringify(data));
        var cb = arg3 || arg2;
        cb = this.wrapper(cb);
        this.sendCount.next(this.sendCount.getValue() + 1);
        this.io.send.call(this.io, data, cb);
    }
    private wrapper(callback: (err: Error) => void): (err: Error) => void {
        return (err: Error): void => {
            callback && callback(err);
            this.sendCount.next(this.sendCount.getValue() - 1);
        };
    }

    public closeGracefully() {
        //let subscription = this.sendCount.observeOn(Rx.Scheduler.asap).subscribe( (value) => {
        let subscription = this.sendCount
            .pipe(observeOn(asapScheduler))
            .subscribe((value) => {
                if (value === 0) {
                    this.io.close();
                    subscription.unsubscribe();
                }
            });
    }
}

export interface IQuery<T = any> {
    /// increase when load complete
    index: number;

    loading: boolean;
    data: IOutputPaging<T> | undefined;
    error: any | undefined;

    /// assign the reload function optionally
    reload?: Function;
}
export function IQueryDefault(): IQuery {
    return {
        index: 0,
        loading: false,
        data: undefined,
        error: undefined,
    };
}

export interface IInputPagingBase {
    page?: number;
    pageSize?: number;
}

export type IInputParamBase = {
    where?: any;
    paging?: IInputPagingBase;
    orderBy?: Array<Record<string, "asc" | "desc">>;
};

export interface IOutputPagingBase {
    total: number;
    totalPages?: number;
}

export type IPaging = IInputPagingBase & IOutputPagingBase;

export type IOrderByUnit = {
    [index: string]: "asc" | "desc";
};

export type IOutputPaging<T> = {
    paging?: IPaging;
    orderBy?: Array<IOrderByUnit>;
} & {
    results: T[];
};

export interface Option {
    paging?: boolean;
    parseObject?: boolean;
}

export interface ValidObject {
    objectId: string;
}

export type InputC<T> = T;
export type OutputC<
    T,
    K extends Option = {
        parseObject: true;
    },
    U = K extends { parseObject: false } ? T : ParseObject<T>,
> = U;

export type InputR<
    T,
    K extends Option = {
        paging: true;
    },
    U = K extends { paging: false } ? Partial<T> : IInputParamBase,
> = U & Partial<ValidObject>;
export type OutputR<
    T,
    K extends Option = {
        paging: true;
        parseObject: true;
    },
    U = K extends { parseObject: false } ? T : ParseObject<T>,
    V = K extends { paging: false } ? U : IOutputPaging<U>,
> = V;

export type InputU<T> = ValidObject & Partial<T>;
export type OutputU<
    T,
    K extends Option = {
        parseObject: true;
    },
    U = K extends { parseObject: false } ? T : ParseObject<T>,
> = U;

export type InputD<T> = InputU<T>;
export type OutputD<
    T,
    K extends Option = {
        parseObject: true;
    },
> = OutputU<T, K>;

/// Server
export type ApisType = "Get" | "Patch" | "Post" | "Put" | "Delete" | "Ws";

/// Server Implement
export interface ApisRequestArg {
    [path: string]: [any, any, boolean];
}
export type ApisRequestBase = { [K in ApisType]?: ApisRequestArg };

type ApisExtractInput<T> = T extends [infer K, infer U, infer V] ? K : never;
type ApisExtractOutput<T> = T extends [infer K, infer U, infer V] ? U : never;
type ApisExtractLoginRequired<T> = T extends [infer K, infer U, infer V] ? V : never;
type ApisSessionRequired = { accessToken: string };

export interface IValServerInputConfig {
    spec?: "GET" | "PATCH" | "POST" | "PUT" | "DELETE";
    /// on error, don't show up error message
    silent?: boolean;
}
export type IValServerInputRDConfig = IValServerInputConfig & {
    spec?: "GET" | "PATCH" | "DELETE";
};
export type IValServerInputCUConfig = IValServerInputConfig & {
    spec?: "POST" | "PATCH" | "PUT" | "DELETE";
};

interface IValServerBaseConfig {
    ip: string;
    port: number;
    ssl?: boolean;
    cgiPath?: string;
    filePath?: string;
    mocking?: boolean;
}
export interface IGeneralRequestError {
    errno: string;
    code: string;
    syscall: string;
    hostname: string;
    host: string;
    port: string;
}

export interface IGeneralRequestRejection {
    err: IGeneralRequestError;
    res: IncomingMessage;
    // body: any;
}

export interface ISubjectResponse {
    method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE";
    uri: string;
    params: any;
    response: any /*object*/;
}

export interface ISubjectError {
    method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE";
    uri: string;
    params: any;
    silent: boolean;
    error: IGeneralRequestRejection;
}

export interface ISubjectUnauthorized {
    method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE";
    uri: string;
    params: any;
    silent: boolean;
    statusCode: number;
    error: any;
}

export interface IListenerDelete {
    path: string;
    objectId: string;
}

export class ValServerBase<
    T extends ApisRequestBase,
    W extends IValServerBaseConfig = IValServerBaseConfig,
> {
    protected config: W;
    protected accessToken: string | null = null;
    protected sjLogined: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private ssl: boolean | undefined;
    constructor(config: W) {
        this.config = config;
    }

    private tryHandleGlobalError(
        method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE",
        uri: string,
        error: any,
        silent: boolean,
    ): boolean {
        if (silent) return false;
        const payload = error?.body ?? error;
        const code = extractErrorCode(payload);
        if (!code) return false;
        const fields = extractErrorFields(payload);
        const globalMessage = resolveGlobalErrorMessage(
            code,
            { uri, method },
            fields,
        );
        if (!globalMessage) return false;

        const holder = VueHolder();
        holder.$alert?.(
            {
                title: holder.$_("w_DataDuplicate"),
                message: holder.$_(globalMessage.key as any, globalMessage.params),
            },
            "warning",
        );
        return true;
    }

    private makeUrl(uri: string, ws: boolean = false): string {
        let { ip, port, ssl, cgiPath } = this.config;
        let protocol = ws ? "ws" : "http";
        if (ssl) protocol += "s";
        return Url.join(`${protocol}://${ip}:${port}`, cgiPath ?? "", uri);
    }

    /// caches of result / errors //////////////////////////////////
    private caches: { [index: string]: Promise<any> } = {};
    private getCacheHash(
        method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE",
        uri: string,
        data: any,
    ): string {
        return `${method} ${uri} ${JSON.stringify(data)}`;
    }
    private getCache(
        method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE",
        uri: string,
        data: any,
    ) {
        return undefined;
        return this.caches[this.getCacheHash(method, uri, data)];
    }
    private setCache(
        method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE",
        uri: string,
        data: any,
        promise: Promise<any>,
    ) {
        return;
        this.caches[this.getCacheHash(method, uri, data)] = promise;
    }
    private withdrawCache(
        method: "GET" | "PATCH" | "POST" | "PUT" | "DELETE",
        uri: string,
        data: any,
    ) {
        return;
        delete this.caches[this.getCacheHash(method, uri, data)];
    }
    public readonly sjResponse: Subject<ISubjectResponse> = new Subject();
    public readonly sjError: Subject<ISubjectError> = new Subject();
    public readonly sjUnauthorized: Subject<ISubjectUnauthorized> = new Subject();
    ////////////////////////////////////////////////////////////////
    logQuery<T extends (...args: any) => any = this["C"] | this["R"]>(
        query: IQuery | undefined,
        queryFn: T,
        ...params: Parameters<T>
    ): ReturnType<T> {
        console.log("Log Query", queryFn.name, params);
        return Promise.reject() as any;
    }
    useQuery<T extends (...args: any) => any = this["C"] | this["R"]>(
        query: IQuery | undefined,
        queryFn: T,
        ...params: Parameters<T>
    ): ReturnType<T> {
        query && Vue.set(query, "loading", true);
        /// don't clean data, maintain the data state until resolved. for the purpose of smooth animation
        /// don't clean error, maintain the error state until resolved. for the purpose of disable search input

        const call = () => {
            const promise = queryFn.apply(this, params);
            promise
                .then((data) => {
                    if (query) {
                        Vue.set(query, "index", query.index + 1);
                        Vue.set(query, "loading", false);
                        Vue.set(query, "data", data);
                        Vue.set(query, "error", undefined);
                    }
                })
                .catch((e) => {
                    if (query) {
                        Vue.set(query, "index", query.index + 1);
                        Vue.set(query, "loading", false);
                        /// don't clean last result
                        // Vue.set(query, "data", undefined);
                        Vue.set(query, "error", e);
                    }
                });
            return promise;
        };

        if (VueHolder().$info.env === "development") {
            return new Promise((resolve) => {
                setTimeout(() => resolve(call()), 0);
            }) as any;
        }
        return call();
    }

    ////////////////////////////////////////////////////////////////
    @cache(1000)
    async C<
        K extends keyof T["Post"],
        U extends ApisExtractInput<T["Post"][K]>,
        V extends ApisExtractOutput<T["Post"][K]>,
        P extends ApisExtractLoginRequired<T["Post"][K]>,
        C extends P extends false ? U : ApisSessionRequired & U,
    >(key: K, data?: U, config: IValServerInputCUConfig = {}): Promise<V> {
        let { spec, silent } = config;
        spec = spec ?? "POST";
        silent = silent ?? false;

        if (!data) data = {} as any;

        /// resolve cache
        let promise: Promise<V> | undefined = this.getCache(spec, key as string, data);
        if (promise) return promise;

        const isFormData =
            typeof FormData !== "undefined" && data instanceof FormData;
        const requestOptions: Parameters<typeof request>[0] = {
            url: this.makeUrl(key as string),
            method: spec,
            json: false,
            body: data,
            headers: isFormData
                ? {
                      /// apply accessToken
                      ...this.applyAccessToken(),
                  }
                : {
                      "Content-Type": "application/json",
                      /// apply accessToken
                      ...this.applyAccessToken(),
                  },
        };
        promise = new Promise<V>((resolve, reject) => {
            request(requestOptions, (err, res, body) => {
                // loading.dispose();
                /// withdraw cache
                this.withdrawCache(spec as any, key as string, data);

                if (err) {
                    const error = { err };
                    const handled = this.tryHandleGlobalError(
                        spec as any,
                        key as string,
                        error,
                        silent,
                    );
                    if (handled) (error as any).handled = true;
                    return reject(error);
                }
                if (!this.isStatusOk((res as any).statusCode)) {
                    const error = { res, body };
                    if ((res as any)?.statusCode === 401) {
                        this.sjUnauthorized.next({
                            method: spec as any,
                            uri: key as any,
                            params: data,
                            silent,
                            statusCode: 401,
                            error,
                        });
                    }
                    const handled = this.tryHandleGlobalError(
                        spec as any,
                        key as string,
                        error,
                        silent,
                    );
                    if (handled) (error as any).handled = true;
                    return reject(error);
                }
                /// handle accessToken
                // body = body ? StringHelper.toObject(body) : {};
                body = body ?? {};
                if (body.accessToken && /login/.test(key as string)) {
                    this.setAccessToken(body.accessToken);
                }
                resolve(body);
            });
        });
        promise
            .then((response) =>
                this.sjResponse.next({
                    method: spec as any,
                    uri: key as any,
                    params: data,
                    response,
                }),
            )
            .catch((error) => {
                /// defer error broadcast so caller catch can mark handled
                setTimeout(() => {
                    this.sjError.next({
                        method: spec as any,
                        uri: key as any,
                        params: data,
                        silent,
                        error,
                    });
                }, 0);
                return;
            });
        this.setCache(spec, key as string, data, promise);
        return promise;
    }

    Url<K extends keyof T["Get"], U extends ApisExtractInput<T["Get"][K]>>(
        key: K,
        data?: U,
    ): string {
        if (!data) data = {} as any;
        let deepTrace = (prevKey: any, data?: any) => {
            if (!data) {
                data = prevKey;
                prevKey = null;
            }
            let isArray = Array.isArray(data);
            return Object.keys(data).reduce<string[]>((final, key) => {
                let value = data[key];
                if (value == undefined) return final;

                /// convertion: Date
                if (value instanceof Date) value = value.toISOString();

                if (typeof value !== "object") {
                    value = encodeURIComponent(value);
                    if (isArray)
                        final.push(prevKey ? `${prevKey}=${value}` : `${key}=${value}`);
                    else
                        final.push(
                            prevKey ? `${prevKey}.${key}=${value}` : `${key}=${value}`,
                        );
                } else final.splice(0, 0, ...deepTrace(key, value));
                return final;
            }, []);
        };
        let params = deepTrace(data).join("&");
        return `${this.makeUrl(key as string, false)}?${params}`;
    }
    UrlFile(): string;
    UrlFile(data?: IInputFileUnit | IInputFileUnit[]): string | null;
    UrlFile(data?: IInputFileUnit | IInputFileUnit[]): string | null {
        const url = this.Url(this.config.filePath as any);
        if (data == undefined) return url;
        if (Array.isArray(data) && data[0] == undefined) return null;

        data = Array.isArray(data) ? data[0] : data;
        return `${url}id=${data.id}`;
    }

    async R<
        K extends keyof T["Patch"],
        U extends ApisExtractInput<T["Patch"][K]>,
        V extends ApisExtractOutput<T["Patch"][K]>,
        P extends ApisExtractLoginRequired<T["Patch"][K]>,
        C extends P extends false ? U : ApisSessionRequired & U,
    >(key: K, data?: U, config: IValServerInputRDConfig = {}): Promise<V> {
        return this.C(key, data, { ...config, spec: "PATCH" }) as any as V;
    }

    async U<
        K extends keyof T["Put"],
        U extends ApisExtractInput<T["Put"][K]>,
        V extends ApisExtractOutput<T["Put"][K]>,
        P extends ApisExtractLoginRequired<T["Put"][K]>,
        C extends P extends false ? U : ApisSessionRequired & U,
    >(key: K, data: U, config: IValServerInputCUConfig = {}): Promise<V> {
        return this.C(key, data, { ...config, spec: "PUT" }) as any as V;
    }

    /// Provide deletion listener
    protected sjD: Subject<IListenerDelete> = new Subject<IListenerDelete>();
    async D<
        K extends keyof T["Delete"],
        U extends ApisExtractInput<T["Delete"][K]>,
        V extends ApisExtractOutput<T["Delete"][K]>,
        P extends ApisExtractLoginRequired<T["Delete"][K]>,
        C extends P extends false ? U : ApisSessionRequired & U,
    >(key: K, data: U, config: IValServerInputRDConfig = {}): Promise<V> {
        let deletion = this.C(key, data, { ...config, spec: "DELETE" }) as V;
        this.sjD.next({ path: key as string, objectId: (data as any).objectId });
        return deletion;
    }
    public listenD<K extends keyof T["Delete"]>(path: string): Observable<string> {
        return this.sjD.pipe(filter((v) => v.path === path)).pipe(map((v) => v.objectId));
    }

    async WS<
        K extends keyof T["Ws"],
        U extends ApisExtractInput<T["Ws"][K]>,
        V extends ApisExtractOutput<T["Ws"][K]>,
        P extends ApisExtractLoginRequired<T["Ws"][K]>,
        C extends P extends false ? U : ApisSessionRequired & U,
    >(key: K): Promise<Socket> {
        return new Promise<Socket>((resolve, reject) => {
            const url = `${this.makeUrl(key as string, true)}?accessToken=${
                this.accessToken
            }`;
            const ws = new WSSocket(url);
            ws.on("error", (err) => {
                reject({ err });
            });
            ws.on("open", async () => {
                let socket = await Socket.get(ws);
                let callback = (data: any) => {
                    let result = JSON.parse(data);
                    do {
                        if (this.isStatusOk(result.statusCode)) {
                            resolve(socket);
                            break;
                        }
                        reject({
                            res: { statusCode: result.statusCode },
                            body: result.message,
                        });
                    } while (0);
                    socket.io.removeListener("message", callback);
                };
                socket.io.addListener("message", callback);
            });
        });
    }

    getAccessToken(): string | null {
        return this.accessToken;
    }

    setAccessToken(value: string) {
        this.accessToken = value;
        this.sjLogined.next(true);
    }

    private static defaultServer: ValServerBase<any> | null = null;
    public static getDefault<T>(this: { new (...args): T }): T {
        let conf: IValServerBaseConfig = !debug.prodMode
            ? Object.assign({}, config)
            : ({
                  port: config.prodPort,
                  cgiPath: config.cgiPath,
                  filePath: config.filePath,
                  loginPath: config.loginPath,
              } as any);
        let { hostname, port, protocol } = window.location;
        conf.ip = conf.ip || hostname;
        conf.ssl = typeof conf.ssl === "boolean" ? conf.ssl : /^https/.test(protocol);
        conf.port = conf.port || +port || (conf.ssl ? 443 : 80);

        return (
            (this as any).defaultServer || ((this as any).defaultServer = new this(conf))
        );
    }
    public static sharedInstance<T>(this: { new (...args): T }): T {
        return (this as any).getDefault();
    }

    protected waitForLogin(): Promise<boolean> {
        return this.sjLogined
            .pipe(filter((v) => v))
            .pipe(first())
            .toPromise();
    }

    public applyAccessToken() {
        /// apply accessToken
        return this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {};
    }

    /// private helper
    private isStatusOk(statusCode: number): boolean {
        return Math.floor(statusCode / 100) === 2;
    }
}
